
import React from "react";
import { Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Download, FileText } from "lucide-react";
import { ImportResult } from "@/services/imports/importTypes";
import MarkdownImportGuide from "@/components/admin/MarkdownImportGuide";

interface AdminHeaderProps {
  isImporting: boolean;
  isImportingMarkdown: boolean;
  importStats: ImportResult | null;
  handleFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  downloadTemplateFile: () => void;
  downloadMarkdownTemplate: () => void;
}

const AdminHeader = ({
  isImporting,
  isImportingMarkdown,
  importStats,
  handleFileUpload,
  downloadTemplateFile,
  downloadMarkdownTemplate,
}: AdminHeaderProps) => {
  return (
    <header className="mb-10">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <Shield className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-3xl font-bold">Admin Dashboard</h1>
            <p className="mt-2 text-muted-foreground">
              Manage prompts, categories, users, and site content
            </p>
          </div>
        </div>
        
        <div className="mt-4 sm:mt-0 flex flex-col sm:flex-row gap-2">
          <div className="flex flex-wrap gap-2">
            <Button 
              variant="outline" 
              className="flex items-center gap-2"
              onClick={downloadTemplateFile}
            >
              <Download className="h-4 w-4" />
              <span>Excel Template</span>
            </Button>
            
            <Button 
              variant="outline" 
              className="flex items-center gap-2"
              onClick={downloadMarkdownTemplate}
            >
              <FileText className="h-4 w-4" />
              <span>Markdown Template</span>
            </Button>
          </div>
          
          <div className="flex gap-2 mt-2 sm:mt-0">
            <label className="cursor-pointer">
              <div className={`flex items-center gap-2 px-4 py-2 border border-border rounded-md ${isImporting || isImportingMarkdown ? 'opacity-70' : 'hover:bg-secondary'}`}>
                <span>{isImporting || isImportingMarkdown ? 'Importing...' : 'Import Prompts'}</span>
                <input 
                  type="file" 
                  accept=".xlsx,.xls,.csv,.md,.txt" 
                  className="hidden"
                  onChange={handleFileUpload}
                  disabled={isImporting || isImportingMarkdown}
                />
              </div>
            </label>
          </div>
        </div>
      </div>
      
      {importStats && (
        <div className={`mt-4 p-4 rounded-md ${importStats.success ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
          <h3 className="font-semibold mb-2">Import Results</h3>
          <p>Successfully imported: {importStats.imported} prompts</p>
          {importStats.failed > 0 && (
            <div className="mt-2">
              <p className="text-red-600 dark:text-red-400">Failed: {importStats.failed} prompts</p>
              {importStats.errors.length > 0 && (
                <div className="mt-2 max-h-40 overflow-y-auto text-sm">
                  <p className="font-medium">Errors:</p>
                  <ul className="list-disc list-inside">
                    {importStats.errors.slice(0, 5).map((error, i) => (
                      <li key={i} className="text-red-600 dark:text-red-400">{error}</li>
                    ))}
                    {importStats.errors.length > 5 && (
                      <li>...and {importStats.errors.length - 5} more errors</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      
      {importStats && importStats.errors.some(err => err.includes('category')) && (
        <MarkdownImportGuide />
      )}
    </header>
  );
};

export default AdminHeader;
